import { defineAsyncComponent, reactive } from 'vue'
import { ENDPOINTS } from '@/lib/consts.js'
import { useCore } from '@/composables/core'
import {
  initialize,
  IterableEmbeddedManager,
  IterableEmbeddedSessionManager,
  trackEmbeddedClick,
  updateUser,
} from '@iterable/web-sdk'
import { PAGE_SOURCES } from '@/lib/analytics/consts.js'
import { notificationAction } from '@/lib/notifications.js'
import { PROMO_BANNER_THEMES } from '@/components/top-bar/consts.js'

const APP_PACKAGE_NAME = 'booksy.com'

let _iterable = reactive({})
const createIterableUserInstance = async () => {
  if (!_iterable.instance) {
    const { apiCall, store, i18n } = useCore()

    const fetchJwtForIterable = async () => {
      const { data } = await apiCall({
        url: ENDPOINTS.ITERABLE_JWT,
      })
      return data.token
    }

    _iterable.embeddedSessionManager = new IterableEmbeddedSessionManager(APP_PACKAGE_NAME)
    _iterable.embeddedManager = new IterableEmbeddedManager(APP_PACKAGE_NAME)

    _iterable.instance = initialize(
      store.state.routingConfig.config.iterable_api_key,
      fetchJwtForIterable
    )

    _iterable.embeddedManager.addUpdateListener({
      onMessagesUpdated: () => {
        /* It is only executed while syncMessages is called.  */
      },
      onEmbeddedMessagingDisabled: () => {
      },
    })

    await _iterable.instance.setEmail(store.state.identity.user.email)

    await updateUser({
      dataFields: {
        locale: i18n.locale,
      },
    })

    _iterable.embeddedManager.syncMessages(
      APP_PACKAGE_NAME,
      () => {}
    )
  } else {
    throw new Error('Iterable instance already created, destroy it first before create new one.')
  }
}

const destroyIterableUserInstance = () => {
  if (_iterable.instance) {
    _iterable.instance.logout()
    _iterable = {}
  }
}
const useIterableBanners = () => {
  if (_iterable.instance) {
    const { embeddedManager, embeddedSessionManager } = _iterable

    return {
      getBannerForPlacement: (pageSource, optionalParams = {}) => {
        const _renderIterableBanner = (placementId) => {
          if (placementId) {
            const [message] = embeddedManager.getMessagesForPlacement(placementId)

            if (message) {
              const { title, body, buttons } = message.elements
              const { theme = PROMO_BANNER_THEMES.GRADIENT, icon } = message.payload ?? {}

              if (!title || !buttons?.[0]?.title) return

              return {
                component: defineAsyncComponent(() => import('@/components/top-bar/promo-banner.vue')),
                options: {
                  titleText: title,
                  description: body,
                  btnTitle: buttons[0].title,
                  theme,
                  icon,
                  ctaAction: () => {
                    if (buttons[0].id && optionalParams.date) {
                      updateUser({
                        mergeNestedObjects: true,
                        dataFields: {
                          embeddedMessagesButtonsClickDates: {
                            [buttons[0].id]: optionalParams.date,
                          },
                        },
                      })
                    }

                    trackEmbeddedClick({
                      messageId: message.metadata.messageId,
                      buttonIdentifier: buttons[0].id,
                      appPackageName: APP_PACKAGE_NAME,
                    })

                    notificationAction({ target_type: buttons[0].action.type })
                  },
                  onMounted: () => {
                    // Impression is total number of times, and total time when banner was visible
                    embeddedSessionManager.startImpression(message.metadata.messageId, placementId)
                  },
                  onUnmounted: () => {
                    embeddedSessionManager.pauseImpression(message.metadata.messageId)
                  },
                },
              }
            }
          }
        }

        const _placementsStrategy = {
          [PAGE_SOURCES.PAYMENTS_AND_CHECKOUT]: () => {
            const placementId = useCore().store.state.routingConfig.config?.embedded_messages_placement_ids?.PAYMENTS_AND_CHECKOUT || null
            return _renderIterableBanner(placementId)
          },
          [PAGE_SOURCES.CALENDAR]: () => {
            const placementId = useCore().store.state.routingConfig.config?.embedded_messages_placement_ids?.CALENDAR || null
            return _renderIterableBanner(placementId)
          },
        }
        return _placementsStrategy?.[pageSource]?.()
      },
      startSession: () => embeddedSessionManager.startSession(),
      endSession: () => embeddedSessionManager.endSession(),
    }
  } else {
    throw new Error('Iterable instance not created.')
  }
}

export {
  createIterableUserInstance,
  destroyIterableUserInstance,
  useIterableBanners,
}
